import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { FiCheck, FiX, FiAlertCircle, FiInfo, FiBell } from 'react-icons/fi';
import './NotificationSystem.css';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    const addNotification = useCallback((notification) => {
        const id = Date.now() + Math.random();
        const newNotification = {
            id,
            type: 'info',
            duration: 5000,
            ...notification
        };

        setNotifications(prev => [...prev, newNotification]);

        if (newNotification.duration > 0) {
            setTimeout(() => {
                removeNotification(id);
            }, newNotification.duration);
        }

        return id;
    }, [removeNotification]);

    const success = useCallback((message, options = {}) => {
        return addNotification({ type: 'success', message, ...options });
    }, [addNotification]);

    const error = useCallback((message, options = {}) => {
        return addNotification({ type: 'error', message, ...options });
    }, [addNotification]);

    const warning = useCallback((message, options = {}) => {
        return addNotification({ type: 'warning', message, ...options });
    }, [addNotification]);

    const info = useCallback((message, options = {}) => {
        return addNotification({ type: 'info', message, ...options });
    }, [addNotification]);

    const value = useMemo(() => ({
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        success,
        error,
        warning,
        info
    }), [notifications, addNotification, removeNotification, clearAllNotifications, success, error, warning, info]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <NotificationContainer />
        </NotificationContext.Provider>
    );
};

const NotificationContainer = () => {
    const { notifications, removeNotification, clearAllNotifications } = useNotifications();

    if (notifications.length === 0) return null;

    return (
        <div className="notification-container">
            {notifications.length > 1 && (
                <div className="notification-header">
                    <span className="notification-count">
                        <FiBell />
                        {notifications.length} notifications
                    </span>
                    <button 
                        className="clear-all-btn"
                        onClick={clearAllNotifications}
                        aria-label="Clear all notifications"
                    >
                        Clear all
                    </button>
                </div>
            )}
            
            <div className="notifications-list">
                {notifications.map((notification) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onRemove={removeNotification}
                    />
                ))}
            </div>
        </div>
    );
};

const NotificationItem = ({ notification, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    useEffect(() => {
        // Animate in
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleRemove = () => {
        setIsRemoving(true);
        setTimeout(() => {
            onRemove(notification.id);
        }, 300);
    };

    const getIcon = () => {
        switch (notification.type) {
            case 'success':
                return <FiCheck />;
            case 'error':
                return <FiX />;
            case 'warning':
                return <FiAlertCircle />;
            case 'info':
            default:
                return <FiInfo />;
        }
    };

    const getProgressBar = () => {
        if (notification.duration <= 0) return null;

        return (
            <div className="notification-progress">
                <div 
                    className="notification-progress-bar"
                    style={{ animationDuration: `${notification.duration}ms` }}
                />
            </div>
        );
    };

    return (
        <div 
            className={`notification-item ${notification.type} ${isVisible ? 'visible' : ''} ${isRemoving ? 'removing' : ''}`}
            role="alert"
            aria-live="polite"
        >
            <div className="notification-content">
                <div className="notification-icon">
                    {getIcon()}
                </div>
                <div className="notification-text">
                    {notification.title && (
                        <h4 className="notification-title">{notification.title}</h4>
                    )}
                    <p className="notification-message">{notification.message}</p>
                </div>
                <button
                    className="notification-close"
                    onClick={handleRemove}
                    aria-label="Close notification"
                >
                    <FiX />
                </button>
            </div>
            {getProgressBar()}
        </div>
    );
};

// Toast notification component for quick messages
export const Toast = ({ message, type = 'info', duration = 3000, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => onClose?.(), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <div className={`toast ${type} ${isVisible ? 'visible' : ''}`}>
            <div className="toast-icon">
                {type === 'success' && <FiCheck />}
                {type === 'error' && <FiX />}
                {type === 'warning' && <FiAlertCircle />}
                {type === 'info' && <FiInfo />}
            </div>
            <span className="toast-message">{message}</span>
        </div>
    );
};

export default NotificationProvider; 